import React, { useContext, useMemo } from "react";
import { AuthContext } from "../../Context/AuthContext";
import useReadAllCharterRequests from "../lib/useReadAllCharterRequests";
import useReadMyMeetings from "../lib/useReadMyMeetings";
import { useReadContactPerson } from "../lib/useReadUpdateContactPerson";
import { calculateWorkflowStep } from "../../Utils/workflowStepCalculator";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FiAlertCircle } from "react-icons/fi";

export default function DashboardHome() {
  const { profileData, profileLoading, user } = useContext(AuthContext);
  const { charterRequests, charterRequestsLoading, charterRequestsError } = useReadAllCharterRequests();
  const { meetings, isLoading: meetingsLoading, isError: meetingsError } = useReadMyMeetings();
  const { data: contactPersonData, isLoading: contactPersonLoading } = useReadContactPerson();
  const navigate = useNavigate();
  
  // Extract contact person data from the nested structure
  const contactPerson = useMemo(() => contactPersonData?.data?.contactPerson || {}, [contactPersonData]);
  
  // Map API step to 12-step display step
  const getDisplayStep = (apiStep) => {
    switch (apiStep) {
      case 1: return 1; // charterRequest (charterer)
      case 2: return 3; // charterResponse (vessel owner)
      case 3: return 4; // processCharterResponse (admin)
      case 4: return 5; // isCharterMovingForward (charterer)
      case 5: return 6; // processCharterMovingForward (admin)
      case 6: return 7; // selectMeetingDate (both)
      case 7: return 8; // meeting result saved (system)
      case 8: return 9; // signVesselNominationAgreement (both)
      case 9: return 10; // processVesselInspectionResult (admin)
      case 10: return 11; // signCharterPartyAgreement (both)
      case 11: return 12; // processCharterPartyAgreement (admin)
      default: return apiStep; // includes 12 -> 12
    }
  };

  const metrics = useMemo(() => {
    if (!charterRequests || charterRequests.length === 0) {
      return { total: 0, active: 0, pending: 0, completed: 0, rows: [] };
    }

    const enriched = charterRequests.map((req) => {
      const apiStep = req.step !== undefined ? req.step : calculateWorkflowStep(req);
      const displayStep = getDisplayStep(apiStep);
      const status = displayStep >= 12 ? "Completed" : displayStep <= 2 ? "Pending" : "Active";
      const origin = req.origin?.coordinates ? req.origin.coordinates.join(", ") : null;
      const destination = req.destination?.coordinates ? req.destination.coordinates.join(", ") : null;
      const shippingPoints = origin && destination ? `${origin} - ${destination}` : "N/A";
      return {
        id: req._id,
        vesselType: req.vessel || "Vessel",
        duration: `${req.duration || 0} Days`,
        pricing: (req.finalProposedPrice || req.pricing) ? `$${req.finalProposedPrice || req.pricing}/day` : "N/A",
        shippingPoints,
        status,
      };
    });

    const total = enriched.length;
    const completed = enriched.filter(r => r.status === "Completed").length;
    const pending = enriched.filter(r => r.status === "Pending").length;
    const active = total - completed - pending;

    return { total, active, pending, completed, rows: enriched.slice(0, 3) };
  }, [charterRequests]);
  
  // Get country flag emoji based on country name
  const getCountryFlag = (countryName) => {
    const countryFlags = {
      "United Kingdom": "🇬🇧",
      "United States": "🇺🇸",
      "Nigeria": "🇳🇬",
      "Canada": "🇨🇦",
      "France": "🇫🇷",
      "Germany": "🇩🇪",
      "Australia": "🇦🇺",
      "China": "🇨🇳",
      "Japan": "🇯🇵",
      "Spain": "🇪🇸",
      "Ghana": "🇬🇭",
      "South Africa": "🇿🇦",
      "Kenya": "🇰🇪",
    };
    return countryFlags[countryName] || "🌍";
  };

  // Get first two initials for avatar
  const userInitials = user?.email
    ? user.email.slice(0, 2).toUpperCase()
    : "US";

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get the latest upcoming meeting
  const getLatestUpcomingMeeting = () => {
    if (!meetings || meetings.length === 0) return null;
    
    const now = new Date();
    const upcomingMeetings = meetings.filter(meeting => {
      const meetingDate = new Date(meeting.meetingDate || meeting.date);
      return meetingDate > now;
    });
    
    if (upcomingMeetings.length === 0) return null;
    
    // Sort by date and get the earliest upcoming meeting
    return upcomingMeetings.sort((a, b) => 
      new Date(a.meetingDate || a.date) - new Date(b.meetingDate || b.date)
    )[0];
  };

  const latestMeeting = getLatestUpcomingMeeting();

  return (
    <div className="vatug-grid-container">
      {/* Company Profile Card */}
      <div className="vatug-profile-card">
        <div className="vatug-profile-banner"></div>
        <div className="vatug-profile-content">
          <div className="vatug-profile-avatar-container">
            <div className="vatug-profile-avatar">{userInitials}</div>
            <div className="vatug-profile-header-info">
              <h2 className="vatug-profile-name">
                {profileLoading ? (
                  <Skeleton width={150} />
                ) : (
                  profileData?.companyName || "Company Name"
                )}
              </h2>
              <div className="vatug-profile-location">
                <span className="vatug-profile-location-flag">
                  {getCountryFlag(profileData?.country)}
                </span>
                {profileLoading ? (
                  <Skeleton width={200} />
                ) : (
                  <>
                    {profileData?.country || "Country"} | {profileData?.phoneNumber || "Phone Number"}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="vatug-profile-stats">
            <div className="vatug-profile-stat">
              <p className="vatug-profile-stat-label">Total Charters</p>
              <p className="vatug-profile-stat-value blue">{charterRequestsLoading ? <Skeleton width={30} /> : metrics.total}</p>
            </div>
            <div className="vatug-profile-stat">
              <p className="vatug-profile-stat-label">Active Charters</p>
              <p className="vatug-profile-stat-value green">{charterRequestsLoading ? <Skeleton width={30} /> : metrics.active}</p>
            </div>
            <div className="vatug-profile-stat">
              <p className="vatug-profile-stat-label">Pending Requests</p>
              <p className="vatug-profile-stat-value amber">{charterRequestsLoading ? <Skeleton width={30} /> : metrics.pending}</p>
            </div>
            <div className="vatug-profile-stat">
              <p className="vatug-profile-stat-label">Completed Charters</p>
              <p className="vatug-profile-stat-value">{charterRequestsLoading ? <Skeleton width={30} /> : metrics.completed}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Person & Upcoming Haggling */}
      <div className="vatug-right-side-container">
        {/* Contact Person */}
        <div className="vatug-card">
          <div className="vatug-card-header">
            <h3 className="vatug-card-title">Contact Person</h3>
          </div>
          <div className="vatug-contact-grid">
            <div>
              <p className="vatug-contact-item-label">Contact name</p>
              <p className="vatug-contact-item-value">
                {contactPersonLoading ? (
                  <Skeleton width={120} />
                ) : (
                  contactPerson?.contactName || "Not provided"
                )}
              </p>
            </div>
            <div>
              <p className="vatug-contact-item-label">Email</p>
              <p className="vatug-contact-item-value">
                {contactPersonLoading ? (
                  <Skeleton width={180} />
                ) : (
                  contactPerson?.emailAddress || "Not provided"
                )}
              </p>
            </div>
            <div>
              <p className="vatug-contact-item-label">Phone</p>
              <p className="vatug-contact-item-value">
                {contactPersonLoading ? (
                  <Skeleton width={150} />
                ) : (
                  contactPerson?.phoneNumber || "Not provided"
                )}
              </p>
            </div>
          </div>
        </div>
        {/* Upcoming Haggling */}
        <div className="vatug-card">
          <h3 className="vatug-card-title">Upcoming Haggling</h3>
          <div className="vatug-haggling-container">
            {meetingsLoading ? (
              <>
                <div>
                  <p className="vatug-contact-item-label">Date</p>
                  <p className="vatug-contact-item-value"><Skeleton width={80} /></p>
                </div>
                <div>
                  <p className="vatug-contact-item-label">Time</p>
                  <p className="vatug-contact-item-value"><Skeleton width={60} /></p>
                </div>
              </>
            ) : meetingsError ? (
              <div style={{ 
                display: "flex", 
                flexDirection: "column", 
                alignItems: "center", 
                justifyContent: "center", 
                padding: "20px",
                textAlign: "center"
              }}>
                <p style={{ color: "#374151", fontWeight: "600", margin: "0" }}>Pending Approval</p>
              </div>
            ) : latestMeeting ? (
              <>
                <div>
                  <p className="vatug-contact-item-label">Date</p>
                  <p className="vatug-contact-item-value">
                    {formatDate(latestMeeting.meetingDate || latestMeeting.date)}
                  </p>
                </div>
                <div>
                  <p className="vatug-contact-item-label">Time</p>
                  <p className="vatug-contact-item-value">
                    {formatTime(latestMeeting.meetingDate || latestMeeting.date)}
                  </p>
                </div>
              </>
            ) : (
              <div style={{ color: "#6B7280", textAlign: "center", padding: "20px" }}>
                No upcoming meetings
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Charter Stats Grid */}
      <div className="vatug-vessel-stats-grid">
        <div className="vatug-vessel-stat-card">
          <h3 className="vatug-vessel-stat-label">Vessels Chartered</h3>
          <p className="vatug-vessel-stat-value">{charterRequestsLoading ? <Skeleton width={30} /> : metrics.total}</p>
        </div>
        <div className="vatug-vessel-stat-card">
          <h3 className="vatug-vessel-stat-label">Active Charters</h3>
          <p className="vatug-vessel-stat-value">{charterRequestsLoading ? <Skeleton width={30} /> : metrics.active}</p>
        </div>
        <div className="vatug-vessel-stat-card">
          <h3 className="vatug-vessel-stat-label">Pending Requests</h3>
          <p className="vatug-vessel-stat-value">{charterRequestsLoading ? <Skeleton width={30} /> : metrics.pending}</p>
        </div>
        <div className="vatug-vessel-stat-card">
          <h3 className="vatug-vessel-stat-label">Completed Charters</h3>
          <p className="vatug-vessel-stat-value">{charterRequestsLoading ? <Skeleton width={30} /> : metrics.completed}</p>
        </div>
      </div>
      
      {/* My Charters */}
      <div className="vatug-charter-card">
        <div className="vatug-charter-header">
          <h3 className="vatug-charter-title">My Charters</h3>
          <button className="vatug-see-all-button" onClick={() => navigate("/charterer-dashboard/ongoing-process")}>See All</button>
        </div>
        <div className="vatug-table-responsive">
          <div className="vatug-table-container">
            <table className="vatug-charter-table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Vessel</th>
                  <th>Shipping Points</th>
                  <th>Duration</th>
                  <th>Pricing</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {charterRequestsLoading ? (
                  <tr>
                    <td colSpan={6}><Skeleton height={20} /></td>
                  </tr>
                ) : charterRequestsError ? (
                  <tr>
                    <td colSpan={6}>
                      <div style={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        alignItems: "center", 
                        justifyContent: "center", 
                        padding: "20px",
                        textAlign: "center"
                      }}>
                        <FiAlertCircle size={24} color="#f59e0b" style={{ marginBottom: "8px" }} />
                        <p style={{ color: "#374151", fontWeight: "600", margin: "0 0 4px 0" }}>Pending Approval</p>
                        <p style={{ color: "#6b7280", fontSize: "14px", margin: "0" }}>Your charter requests are being reviewed.</p>
                      </div>
                    </td>
                  </tr>
                ) : metrics.rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ color: "#6B7280" }}>No charters yet.</td>
                  </tr>
                ) : (
                  metrics.rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="vatug-vessel-cell">
                          <div className="vatug-vessel-image">
                            <img
                              src="https://res.cloudinary.com/du44pwjoi/image/upload/v1742471097/csv_klkqpb.png"
                              alt="Vessel"
                              className="vatug-object-cover"
                            />
                          </div>
                          <span className="vatug-vessel-name">#{row.id.slice(-8)}</span>
                        </div>
                      </td>
                      <td className="vatug-table-cell">{row.vesselType}</td>
                      <td className="vatug-table-cell">{row.shippingPoints}</td>
                      <td className="vatug-table-cell">{row.duration}</td>
                      <td className="vatug-table-cell">{row.pricing}</td>
                      <td>
                        <span className={`vatug-status-badge ${row.status === 'Completed' ? 'completed' : row.status === 'Pending' ? 'pending' : 'active'}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
} 